import { getNestedOrFloatingElementById, getSexTranslatedOrOrig } from '@fs/zion-sls'
import uniqBy from 'lodash.uniqby'

/**
 * Returns the value of the active field info from the useSlsHighlights hook.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.activeFieldInfo - The active field info object from the activeField object from the hook.
 * @param {Function} params.t - Translation function for internationalization.
 * @returns {String|null} - Return the value of the field in string or null.
 */
export const getFieldValue = ({ activeFieldInfo, t }) => {
  const { fieldType, value, fieldValues } = activeFieldInfo || {}

  if (fieldType === 'SEX_CODE') {
    const sexValue = getSexTranslatedOrOrig(activeFieldInfo, t)
    return sexValue.primaryText || sexValue
  }

  if (fieldType === 'EVENT_TYPE') {
    return activeFieldInfo?.label
  }

  if (value) {
    if (value === fieldType && fieldValues?.length > 0 && fieldValues[0].origValue?.text) {
      return fieldValues[0].origValue.text
    }
    if (value.standard?.standardizedText) {
      return value.standard.standardizedText
    }
    if (value.originalText) {
      return value.originalText
    }
    if (value.value) {
      return value.value
    }
    if (value !== fieldType) {
      return value
    }
  }

  return null
}

/**
 * Checks if any of the selected highlights are shared between multiple names.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Array} params.mainSelectedHighlights - The main highlights.
 * @param {Array} params.splitScreenSelectedHighlights - The split screen highlights.
 * @param {Object} params.types - Array of zion SLS types.
 * @param {string} params.fieldType - The type of the field.
 * @param {string} params.personId - The ID of the selected person.
 * @returns {boolean} - Returns true if any highlight is shared and either the field or element is shareable.
 */
export const checkIfSharedHighlights = ({
  mainSelectedHighlights,
  splitScreenSelectedHighlights,
  types,
  fieldType,
  personId,
}) => {
  const attachedHighlights = [...mainSelectedHighlights, ...splitScreenSelectedHighlights]
  const typeOfField = types.fields.find((type) => type.fieldType === fieldType)
  const fieldIsShareable = !typeOfField?.singleSuper

  const elementIsShareable = !types.elements.find((type) => type.elementType === typeOfField.elementTypes[0])
    ?.singleSuper

  const anyHighlightIsTaken = attachedHighlights?.find((highlight) => {
    // Filtering empty, null or undefined personIds
    const originalPersonIds = highlight.originalHighlight?.personIds.filter(Boolean)

    return originalPersonIds?.length > 0 && !originalPersonIds?.includes(personId)
  })

  return !!anyHighlightIsTaken && (fieldIsShareable || elementIsShareable)
}

/**
 * Extracts fields from shared highlights.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Array} params.highlights - The array of highlights.
 * @param {Array} params.images - Array of zion SLS images.
 * @param {Object} params.types - Array of zion SLS types.
 * @returns {Array} - Returns an array of fields associated with the shared highlights.
 */
export const getFieldsFromSharedHighlights = ({ highlights, images, types }) => {
  const fieldsOnHighlights = highlights.reduce((acc, highlight) => {
    if (highlight?.originalHighlight?.personIds?.length > 0) {
      // Get the shareable-level element for the field associated to the highlight
      let fieldOnHighlight = getNestedOrFloatingElementById(images, highlight?.originalHighlight?.fieldId)
      // Since highlights are on fields, we are at most only going to need to go up one level to find a shared field
      if (fieldOnHighlight?.typeData?.singleSuper)
        fieldOnHighlight = getNestedOrFloatingElementById(images, fieldOnHighlight?.superElements[0]?.id)
      acc.push(
        fieldOnHighlight || {
          id: highlight?.originalHighlight?.fieldId,
          elementType: 'FIELD',
          fieldType: highlight?.originalHighlights?.fieldType,
          label: types?.fields.find((type) => type.fieldType === highlight?.originalHighlight?.fieldType).displayLabel,
        }
      )
    }
    return acc
  }, [])

  return uniqBy(fieldsOnHighlights, (f) => f.id)
}
